import { Pressable, Text, View } from 'react-native';
import { useTheme } from '~/theme/ThemeProvider';

/** Two letters from a display name, or one from an address. Never more. */
export function initialsOf(who: string): string {
  const name = who.trim();
  if (!name) return '?';
  const local = name.includes('@') ? name.slice(0, name.indexOf('@')) : name;
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

/**
 * The account control in the top bar: initials in an ink disc inside an outlined pill.
 *
 * The frame draws a chevron beside the initials, implying a menu. There is no menu — the
 * only thing an operator can do with their account here is leave — so the chevron is not
 * drawn and the pill performs that one action directly. A chevron pointing at nothing is
 * a worse deviation than a missing chevron.
 */
export function AccountPill({ who, onPress }: { who: string; onPress: () => void }) {
  const { c, t, radius, space, touch } = useTheme();
  return (
    <Pressable
      testID="account-pill"
      accessibilityRole="button"
      // Names the ACTION, because that is what activating it does. The address is in the
      // hint so a screen-reader user still hears which account they are about to leave.
      accessibilityLabel="Sign out"
      accessibilityHint={who ? `Signed in as ${who}` : undefined}
      onPress={onPress}
      style={{
        height: touch.min,
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.sm,
        paddingLeft: space.xs,
        paddingRight: space.md,
        borderRadius: radius.pill,
        borderWidth: 1,
        borderColor: c.border2,
        backgroundColor: c.surface,
      }}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: radius.pill,
          backgroundColor: c.inverse,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={[t.sub, { fontWeight: '600', color: c.onInverse }]}>{initialsOf(who)}</Text>
      </View>
      <Text style={[t.body, { color: c.text }]}>Sign out</Text>
    </Pressable>
  );
}
